// ... existing imports ...
import { useEffect, useState, useRef, useMemo } from "react";
import Header from "../components/Header";
import { FilterPanel } from "../components/revenue/FilterPanel";
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import { Search } from 'lucide-react';
import { useData } from "../context/DataContext";
import { useCacheCheck } from "../hooks/useCacheCheck";
import { CacheStatus } from "../components/CacheStatus";
import { RevenueGraph } from '../components/revenue/RevenueGraph';
import { ExportButton } from "../components/ExportButton";

// Add TypeScript declaration for window object
declare global {
  interface Window {
    allTeamMembers?: any[];
  }
}

// Add sort configuration type
type SortConfig = {
  key: string;
  direction: 'asc' | 'desc';
};

// Add this helper function at the top with other helpers
function formatCurrency(amount: number): string {
  const formattedNumber = Math.round(amount)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${formattedNumber}€`;
}

// Define column widths for a more data-focused layout
const clientColumnWidth = 'minmax(120px, 1.2fr)';
const bookedColumnWidth = 'minmax(100px, 1fr)';
const revenueColumnWidth = 'minmax(160px, 2fr)';
const diffColumnWidth = 'minmax(120px, 1fr)';

// --- Main Component ---
export default function MaterializationPerClient() {
  const [searchTerm, setSearchTerm] = useState('');
  // State for year selectors
  const currentYear = new Date().getFullYear();
  const [yearX, setYearX] = useState<number>(currentYear);
  const [yearY, setYearY] = useState<number>(currentYear - 1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'manager', direction: 'asc' });

  const { data, loadingProgress } = useData();
  
  // Check cache when component mounts
  useCacheCheck();

  // Available options (derived from data)
  const [availableCreationYears, setAvailableCreationYears] = useState<number[]>([]);
  const [availableArrivalYears, setAvailableArrivalYears] = useState<number[]>([]);
  const [availableManagers, setAvailableManagers] = useState<string[]>([]);
  const [availableSegments, setAvailableSegments] = useState<string[]>([]);
  const [clients, setClients] = useState<string[]>([]);
  const [hotels, setHotels] = useState<string[]>([]);
  const [hotelGroups, setHotelGroups] = useState<string[]>([]);
  const [markets, setMarkets] = useState<string[]>([]);

  // Applied state (what's currently filtering the data)
  const [appliedCreationYears, setAppliedCreationYears] = useState<Set<number>>(new Set());
  const [appliedArrivalYears, setAppliedArrivalYears] = useState<Set<number>>(new Set());
  const [appliedManagers, setAppliedManagers] = useState<Set<string>>(new Set());
  const [appliedSegments, setAppliedSegments] = useState<Set<string>>(new Set());
  const [appliedClients, setAppliedClients] = useState<Set<string>>(new Set());
  const [appliedHotels, setAppliedHotels] = useState<Set<string>>(new Set());
  const [appliedHotelGroups, setAppliedHotelGroups] = useState<Set<string>>(new Set());
  const [appliedArrivalMonths, setAppliedArrivalMonths] = useState<Set<number>>(new Set());
  const [appliedArrivalQuarters, setAppliedArrivalQuarters] = useState<Set<number>>(new Set());
  const [appliedCreationMonths, setAppliedCreationMonths] = useState<Set<number>>(new Set());
  const [appliedCreationQuarters, setAppliedCreationQuarters] = useState<Set<number>>(new Set());
  const [appliedMarkets, setAppliedMarkets] = useState<Set<string>>(new Set());

  // Pending state (what user is selecting in UI - not yet applied)
  const [pendingCreationYears, setPendingCreationYears] = useState<Set<number>>(new Set());
  const [pendingArrivalYears, setPendingArrivalYears] = useState<Set<number>>(new Set());
  const [pendingManagers, setPendingManagers] = useState<Set<string>>(new Set());
  const [pendingSegments, setPendingSegments] = useState<Set<string>>(new Set());
  const [pendingClients, setPendingClients] = useState<Set<string>>(new Set());
  const [pendingHotels, setPendingHotels] = useState<Set<string>>(new Set());
  const [pendingHotelGroups, setPendingHotelGroups] = useState<Set<string>>(new Set());
  const [pendingArrivalMonths, setPendingArrivalMonths] = useState<Set<number>>(new Set());
  const [pendingArrivalQuarters, setPendingArrivalQuarters] = useState<Set<number>>(new Set());
  const [pendingCreationMonths, setPendingCreationMonths] = useState<Set<number>>(new Set());
  const [pendingCreationQuarters, setPendingCreationQuarters] = useState<Set<number>>(new Set());
  const [pendingMarkets, setPendingMarkets] = useState<Set<string>>(new Set());

  // Add state for virtual all selection
  const [allClientsSelected, setAllClientsSelected] = useState(true);
  const [allHotelGroupsSelected, setAllHotelGroupsSelected] = useState(true);
  const [allHotelsSelected, setAllHotelsSelected] = useState(true);
  const [allMarketsSelected, setAllMarketsSelected] = useState(true);

  // Update selection handlers for pending filters
  const handleClientsChange = (newSelected: Set<string>) => {
    setPendingClients(newSelected);
    setAllClientsSelected(newSelected.size === 0);
  };

  const handleHotelGroupsChange = (newSelected: Set<string>) => {
    setPendingHotelGroups(newSelected);
    setAllHotelGroupsSelected(newSelected.size === 0);
  };

  const handleHotelsChange = (newSelected: Set<string>) => {
    setPendingHotels(newSelected);
    setAllHotelsSelected(newSelected.size === 0);
  };

  const handleMarketsChange = (newSelected: Set<string>) => {
    setPendingMarkets(newSelected);
    setAllMarketsSelected(newSelected.size === 0);
  };

  // Detect changes between pending and applied state
  const hasUnappliedChanges = useMemo(() => {
    const setsEqual = (a: Set<any>, b: Set<any>) => 
      a.size === b.size && [...a].every(x => b.has(x));
    
    return !setsEqual(pendingCreationYears, appliedCreationYears) ||
           !setsEqual(pendingArrivalYears, appliedArrivalYears) ||
           !setsEqual(pendingManagers, appliedManagers) ||
           !setsEqual(pendingSegments, appliedSegments) ||
           !setsEqual(pendingClients, appliedClients) ||
           !setsEqual(pendingHotels, appliedHotels) ||
           !setsEqual(pendingHotelGroups, appliedHotelGroups) ||
           !setsEqual(pendingArrivalMonths, appliedArrivalMonths) ||
           !setsEqual(pendingArrivalQuarters, appliedArrivalQuarters) ||
           !setsEqual(pendingCreationMonths, appliedCreationMonths) ||
           !setsEqual(pendingCreationQuarters, appliedCreationQuarters) ||
           !setsEqual(pendingMarkets, appliedMarkets);
  }, [
    pendingCreationYears, appliedCreationYears,
    pendingArrivalYears, appliedArrivalYears,
    pendingManagers, appliedManagers,
    pendingSegments, appliedSegments,
    pendingClients, appliedClients,
    pendingHotels, appliedHotels,
    pendingHotelGroups, appliedHotelGroups,
    pendingArrivalMonths, appliedArrivalMonths,
    pendingArrivalQuarters, appliedArrivalQuarters,
    pendingCreationMonths, appliedCreationMonths,
    pendingCreationQuarters, appliedCreationQuarters,
    pendingMarkets, appliedMarkets,
  ]);

  // Apply filters - copy pending to applied
  const handleApplyFilters = () => {
    setAppliedCreationYears(new Set(pendingCreationYears));
    setAppliedArrivalYears(new Set(pendingArrivalYears));
    setAppliedManagers(new Set(pendingManagers));
    setAppliedSegments(new Set(pendingSegments));
    setAppliedClients(new Set(pendingClients));
    setAppliedHotels(new Set(pendingHotels));
    setAppliedHotelGroups(new Set(pendingHotelGroups));
    setAppliedArrivalMonths(new Set(pendingArrivalMonths));
    setAppliedArrivalQuarters(new Set(pendingArrivalQuarters));
    setAppliedCreationMonths(new Set(pendingCreationMonths));
    setAppliedCreationQuarters(new Set(pendingCreationQuarters));
    setAppliedMarkets(new Set(pendingMarkets));
  };

  // --- NEW: Always initialize months/quarters to all values on very first render ---
  useEffect(() => {
    const allMonths = new Set([1,2,3,4,5,6,7,8,9,10,11,12]);
    const allQuarters = new Set([1,2,3,4]);
    setPendingCreationMonths(allMonths);
    setPendingCreationQuarters(allQuarters);
    setPendingArrivalMonths(allMonths);
    setPendingArrivalQuarters(allQuarters);
    setAppliedCreationMonths(allMonths);
    setAppliedCreationQuarters(allQuarters);
    setAppliedArrivalMonths(allMonths);
    setAppliedArrivalQuarters(allQuarters);
  }, []);

  // Add initialization refs for filters that depend on data
  const filtersInitialized = useRef(false);

  // Ensure all filters are initialized with all options selected after data loads (only on first load)
  useEffect(() => {
    if (filtersInitialized.current) return;
    if (availableManagers.length === 0 || availableSegments.length === 0 || 
        availableCreationYears.length === 0 || availableArrivalYears.length === 0 ||
        clients.length === 0 || hotels.length === 0 || hotelGroups.length === 0 ||
        markets.length === 0) {
      return; // Wait for all data to load
    }

    // Initialize filters: All with ALL values selected
    const allManagers = new Set(availableManagers);
    const allSegments = new Set(availableSegments);
    const allCreationYears = new Set(availableCreationYears);
    const allArrivalYears = new Set(availableArrivalYears);
    const allClients = new Set(clients);
    const allHotels = new Set(hotels);
    const allHotelGroups = new Set(hotelGroups);
    const allMarkets = new Set(markets);

    setPendingManagers(allManagers);
    setPendingSegments(allSegments);
    setPendingClients(allClients);
    setPendingHotels(allHotels);
    setPendingHotelGroups(allHotelGroups);
    setPendingMarkets(allMarkets);
    setPendingCreationYears(allCreationYears);
    setPendingArrivalYears(allArrivalYears);

    setAppliedManagers(allManagers);
    setAppliedSegments(allSegments);
    setAppliedClients(allClients);
    setAppliedHotels(allHotels);
    setAppliedHotelGroups(allHotelGroups);
    setAppliedMarkets(allMarkets);
    setAppliedCreationYears(allCreationYears);
    setAppliedArrivalYears(allArrivalYears);
    
    // Update "all selected" flags
    setAllClientsSelected(false); // Explicitly selected all
    setAllHotelsSelected(false); // Explicitly selected all
    setAllHotelGroupsSelected(false); // Explicitly selected all
    setAllMarketsSelected(false); // Explicitly selected all

    filtersInitialized.current = true;
  }, [availableManagers, availableSegments, clients, hotels, hotelGroups, markets, availableCreationYears, availableArrivalYears]);

  // After setData(allData); in fetchData or after data is loaded
  useEffect(() => {
    if (!data || data.length === 0) return;

    // Years
    const creationYears = Array.from(new Set(data.map(row => row.request_year).filter(Boolean))).sort((a, b) => b - a);
    const arrivalYears = Array.from(new Set(data.map(row => row.arrival_year).filter(Boolean))).sort((a, b) => b - a);

    // Managers
    const managers = Array.from(new Set(data.map(row => row.request_manager).filter(Boolean))).sort();

    // Segments
    const segments = Array.from(new Set(data.map(row => row.segment).filter(Boolean))).sort();

    // Clients
    const allClients = Array.from(new Set(data.map((row: any) => row.company).filter(Boolean))).sort();

    // Hotels/Venues
    const venues = Array.from(new Set(data.map(row => row.venue).filter(Boolean))).sort();

    // Hotel Groups
    let groups = Array.from(new Set(data.map(row => row.hotel_group_name).filter(Boolean))).sort();
    const hasNoGroup = data.some(row => !row.hotel_group_name);
    if (hasNoGroup) {
      groups = [...groups, 'No Group'];
    }

    // Markets
    let marketsList = Array.from(new Set(data.map(row => row.market).filter(Boolean))).sort();
    const hasNoMarket = data.some(row => !row.market);
    if (hasNoMarket) {
      marketsList = [...marketsList, 'No Market'];
    }

    setAvailableCreationYears(creationYears);
    setAvailableArrivalYears(arrivalYears);
    
    // Set initial years based on available data
    if (creationYears.length > 0) {
      const mostRecentYear = creationYears[0]; // Since it's sorted descending
      if (creationYears.includes(currentYear)) {
        setYearX(currentYear);
        // Set yearY to the previous year if available, otherwise the most recent year
        const previousYear = currentYear - 1;
        if (creationYears.includes(previousYear)) {
          setYearY(previousYear);
        } else {
          setYearY(mostRecentYear);
        }
      } else {
        setYearX(mostRecentYear);
        // Set yearY to the second most recent year if available
        if (creationYears.length > 1) {
          setYearY(creationYears[1]);
        } else {
          setYearY(mostRecentYear);
        }
      }
    }
    
    setAvailableManagers(managers);
    setAvailableSegments(segments);
    setClients(allClients);
    setHotels(venues);
    setHotelGroups(groups);
    setMarkets(marketsList);
  }, [data]);

  // Helper to check if a filter is in 'all' state (empty set or all options selected)
  function isAllSelected<T>(selected: Set<T>, allOptions: T[]) {
    return selected.size === 0 || selected.size === allOptions.length;
  }

  // Manager filter is active if size > 0 (managers don't have "all by default")
  function isManagerFilterActive(selectedManagers: Set<string>, availableManagers: string[]) {
    return selectedManagers.size > 0 && selectedManagers.size !== availableManagers.length;
  }

  // Compute possible managers based on all other filters (cascading logic)

  // Repeat this pattern for all other possible... sets:
  // possibleHotelGroups, possibleHotels, possibleClients, possibleCreationYears, possibleCreationMonths, possibleCreationQuarters, possibleArrivalYears, possibleArrivalMonths, possibleArrivalQuarters

  // Date filters

  // Prune date filter sets when possible options change, but only update if changed and only if selection is non-empty and possible set is non-empty

  // Prune and auto-select possible values for Clients, Hotel Groups, and Hotels

  // Restore resetFilters function
  const resetFilters = () => {
    // Reset all filters to all values
    const allManagers = new Set(availableManagers);
    const allSegments = new Set(availableSegments);
    const allCreationYears = new Set(availableCreationYears);
    const allArrivalYears = new Set(availableArrivalYears);
    const allClients = new Set(clients);
    const allHotels = new Set(hotels);
    const allHotelGroups = new Set(hotelGroups);
    const allMarkets = new Set(markets);
    const allMonths = new Set([1,2,3,4,5,6,7,8,9,10,11,12]);
    const allQuarters = new Set([1,2,3,4]);
    
    // Reset pending state
    setPendingCreationYears(allCreationYears);
    setPendingArrivalYears(allArrivalYears);
    setPendingManagers(allManagers);
    setPendingSegments(allSegments);
    setPendingClients(allClients);
    setPendingHotels(allHotels);
    setPendingHotelGroups(allHotelGroups);
    setPendingMarkets(allMarkets);
    setPendingArrivalMonths(allMonths);
    setPendingArrivalQuarters(allQuarters);
    setPendingCreationMonths(allMonths);
    setPendingCreationQuarters(allQuarters);
    
    // Also reset applied state (so data refreshes immediately)
    setAppliedCreationYears(allCreationYears);
    setAppliedArrivalYears(allArrivalYears);
    setAppliedManagers(allManagers);
    setAppliedSegments(allSegments);
    setAppliedClients(allClients);
    setAppliedHotels(allHotels);
    setAppliedHotelGroups(allHotelGroups);
    setAppliedMarkets(allMarkets);
    setAppliedArrivalMonths(allMonths);
    setAppliedArrivalQuarters(allQuarters);
    setAppliedCreationMonths(allMonths);
    setAppliedCreationQuarters(allQuarters);
    
    // Update "all selected" flags
    setAllClientsSelected(false);
    setAllHotelsSelected(false);
    setAllHotelGroupsSelected(false);
    setAllMarketsSelected(false);
  };

  // Filtered data using the search term and all other filters
  const filteredData = useMemo(() => {
    return data.filter(row => {
      // Search filter
      if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        const matchesSearch = 
          (row.company && row.company.toLowerCase().includes(lower)) ||
          (row.request_manager && row.request_manager.toLowerCase().includes(lower)) ||
          (row.venue && row.venue.toLowerCase().includes(lower)) ||
          (row.segment && row.segment.toLowerCase().includes(lower));
        if (!matchesSearch) return false;
      }

      // FilterPanel filters (use applied state)
      if (isManagerFilterActive(appliedManagers, availableManagers) && !appliedManagers.has(row.request_manager)) return false;
      if (!isAllSelected(appliedClients, clients) && !appliedClients.has(row.company)) return false;
      if (!isAllSelected(appliedArrivalYears, availableArrivalYears) && !appliedArrivalYears.has(row.arrival_year)) return false;
      if (!isAllSelected(appliedCreationYears, availableCreationYears) && !appliedCreationYears.has(row.request_year)) return false;
      if (!isAllSelected(appliedSegments, availableSegments) && !appliedSegments.has(row.segment)) return false;
      const groupName = row.hotel_group_name ? row.hotel_group_name : 'No Group';
      if (!isAllSelected(appliedHotelGroups, hotelGroups) && !appliedHotelGroups.has(groupName)) return false;
      if (!isAllSelected(appliedHotels, hotels) && !appliedHotels.has(row.venue)) return false;
      if (!isAllSelected(appliedArrivalMonths, [1,2,3,4,5,6,7,8,9,10,11,12]) && !appliedArrivalMonths.has(row.arrival_month)) return false;
      if (!isAllSelected(appliedArrivalQuarters, [1,2,3,4]) && !appliedArrivalQuarters.has(row.arrival_quarter)) return false;
      if (!isAllSelected(appliedCreationMonths, [1,2,3,4,5,6,7,8,9,10,11,12]) && !appliedCreationMonths.has(row.request_month)) return false;
      if (!isAllSelected(appliedCreationQuarters, [1,2,3,4]) && !appliedCreationQuarters.has(row.request_quarter)) return false;
      const marketName = row.market ? row.market : 'No Market';
      if (!isAllSelected(appliedMarkets, markets) && !appliedMarkets.has(marketName)) return false;

      return true;
    });
  }, [data, searchTerm, appliedManagers, availableManagers, appliedClients, clients, appliedArrivalYears, availableArrivalYears, appliedCreationYears, availableCreationYears, appliedSegments, availableSegments, appliedHotelGroups, hotelGroups, appliedHotels, hotels, appliedArrivalMonths, appliedArrivalQuarters, appliedCreationMonths, appliedCreationQuarters, appliedMarkets, markets]);

  // Group data by manager, then client
  const groupedData = useMemo(() => {
    // Structure: { [manager]: { [client]: [rows] } }
    const result: Record<string, Record<string, any[]>> = {};
    filteredData.forEach(row => {
      if (!result[row.request_manager]) result[row.request_manager] = {};
      if (!result[row.request_manager][row.company]) result[row.request_manager][row.company] = [];
      result[row.request_manager][row.company].push(row);
    });
    return result;
  }, [filteredData]);

  // --- Render helpers ---
  const [expandedManagers, setExpandedManagers] = useState<Set<string>>(new Set());
  
  // Handle sort click
  const handleSort = (key: string) => {
    setSortConfig(current => ({
      key,
      direction: current?.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort data function
  const sortData = (data: any[]) => {
    return [...data].sort((a, b) => {
      let aValue: any = a[sortConfig.key];
      let bValue: any = b[sortConfig.key];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle different data types
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle strings
      const aString = String(aValue).toLowerCase();
      const bString = String(bValue).toLowerCase();
      return sortConfig.direction === 'asc' 
        ? aString.localeCompare(bString) 
        : bString.localeCompare(aString);
    });
  };
  // Create flat data structure for sorting and total calculation
  const flatData = useMemo(() => {
    const data: any[] = [];
    Object.entries(groupedData).forEach(([manager, clients]) => {
      const allManagerRows = Object.values(clients).flat() as any[];
      const bookedX = allManagerRows.filter((r: any) => r.status === 'Booked' && r.request_year === yearX).length;
      const bookedY = allManagerRows.filter((r: any) => r.status === 'Booked' && r.request_year === yearY).length;
      const estX = allManagerRows.filter((r: any) => r.status === 'Booked' && r.request_year === yearX).reduce((sum: number, r: any) => sum + (r.estimated_revenue || 0), 0);
      const estY = allManagerRows.filter((r: any) => r.status === 'Booked' && r.request_year === yearY).reduce((sum: number, r: any) => sum + (r.estimated_revenue || 0), 0);
      const realX = allManagerRows.filter((r: any) => r.status === 'Booked' && r.request_year === yearX).reduce((sum: number, r: any) => sum + (r.realised_revenue || 0), 0);
      const realY = allManagerRows.filter((r: any) => r.status === 'Booked' && r.request_year === yearY).reduce((sum: number, r: any) => sum + (r.realised_revenue || 0), 0);
      const diffEst = estX - estY;
      const diffReal = realX - realY;
      let diffEstPercent = estY !== 0 ? ((diffEst / estY) * 100) : null;
      let diffRealPercent = realY !== 0 ? ((diffReal / realY) * 100) : null;
      
      if (bookedX === 0 && estX === 0 && estY === 0 && realX === 0 && realY === 0) return;
      
      data.push({
        manager,
        clients,
        bookedX,
        bookedY,
        estX,
        estY,
        realX,
        realY,
        diffEst,
        diffReal,
        diffEstPercent,
        diffRealPercent
      });
    });
    return data;
  }, [groupedData, yearX, yearY]);

  // Apply sorting
  const sortedData = useMemo(() => sortData(flatData), [flatData, sortConfig]);

  // Calculate totals
  const totals = useMemo(() => {
    return sortedData.reduce((acc, row) => {
      acc.bookedX += (row.bookedX || 0);
      acc.bookedY += (row.bookedY || 0);
      acc.estX += (row.estX || 0);
      acc.estY += (row.estY || 0);
      acc.realX += (row.realX || 0);
      acc.realY += (row.realY || 0);
      acc.diffEst += (row.diffEst || 0);
      acc.diffReal += (row.diffReal || 0);
      return acc;
    }, {
      bookedX: 0,
      bookedY: 0,
      estX: 0,
      estY: 0,
      realX: 0,
      realY: 0,
      diffEst: 0,
      diffReal: 0
    });
  }, [sortedData]);

  // Table data transformer for export (level 1 only - managers)
  const getTableDataForExport = () => {
    const tableData = sortedData.map(({ manager, bookedX, bookedY, estX, estY, realX, realY, diffEst, diffReal, diffEstPercent, diffRealPercent }) => ({
      Manager: manager || 'Unassigned',
      [`Created Groups ${yearX}`]: bookedX,
      [`Created Groups ${yearY}`]: bookedY,
      [`Estimated Revenue ${yearX}`]: estX,
      [`Estimated Revenue ${yearY}`]: estY,
      [`Confirmed Revenue ${yearX}`]: realX,
      [`Confirmed Revenue ${yearY}`]: realY,
      'Estimated Diff': diffEst,
      'Estimated Diff %': diffEstPercent !== null ? diffEstPercent : 0,
      'Confirmed Diff': diffReal,
      'Confirmed Diff %': diffRealPercent !== null ? diffRealPercent : 0
    }));

    // Add TOTAL row if there are 2 or more rows
    if (sortedData.length >= 2) {
      const totalDiffEstPercent = totals.estY !== 0 ? ((totals.diffEst / totals.estY) * 100) : 0;
      const totalDiffRealPercent = totals.realY !== 0 ? ((totals.diffReal / totals.realY) * 100) : 0;

      tableData.push({
        Manager: 'TOTAL',
        [`Created Groups ${yearX}`]: totals.bookedX,
        [`Created Groups ${yearY}`]: totals.bookedY,
        [`Estimated Revenue ${yearX}`]: totals.estX,
        [`Estimated Revenue ${yearY}`]: totals.estY,
        [`Confirmed Revenue ${yearX}`]: totals.realX,
        [`Confirmed Revenue ${yearY}`]: totals.realY,
        'Estimated Diff': totals.diffEst,
        'Estimated Diff %': totalDiffEstPercent,
        'Confirmed Diff': totals.diffReal,
        'Confirmed Diff %': totalDiffRealPercent
      });
    }

    return tableData;
  };

  function renderRows() {
    const rows: JSX.Element[] = [];
    
    sortedData.forEach(({ manager, clients, bookedX, bookedY, estX, estY, realX, realY, diffEst, diffReal, diffEstPercent, diffRealPercent }) => {
      if (bookedX === 0 && estX === 0 && estY === 0 && realX === 0 && realY === 0) return;
      const isManagerExpanded = expandedManagers.has(manager);
      rows.push(
        <tr key={manager} className="bg-gray-50 hover:bg-gray-100 hover:cursor-pointer" onClick={() => {
          setExpandedManagers(prev => {
            const next = new Set(prev);
            if (next.has(manager)) next.delete(manager); else next.add(manager);
            return next;
          });
        }}>
          <td className="px-2 py-2 whitespace-nowrap text-sm font-semibold text-gray-900 min-w-[200px]">
            {manager}
          </td>
          <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{bookedX}</td>
          <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{bookedY}</td>
          <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{formatCurrency(estX)}</td>
          <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{formatCurrency(estY)}</td>
          <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{formatCurrency(realX)}</td>
          <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{formatCurrency(realY)}</td>
          <td className="px-2 py-2 whitespace-nowrap text-xs">
            <div className="flex flex-row items-center gap-2 justify-end">
              <span>{formatCurrency(diffEst)}</span>
              {diffEstPercent !== null ? (
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-center ${diffEstPercent > 0 ? 'bg-green-100 text-green-800' : diffEstPercent < 0 ? 'bg-red-100 text-red-800' : ''}`}
                  style={{ minWidth: '48px', width: '48px', display: 'inline-block' }}>
                  {Math.round(diffEstPercent)}%
                </span>
              ) : (
                <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold text-center bg-gray-100 text-gray-400"
                  style={{ minWidth: '48px', width: '48px', display: 'inline-block' }}>
                  -
                </span>
              )}
            </div>
          </td>
          <td className="px-2 py-2 whitespace-nowrap text-xs">
            <div className="flex flex-row items-center gap-2 justify-end">
              <span>{formatCurrency(diffReal)}</span>
              {diffRealPercent !== null ? (
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-center ${diffRealPercent > 0 ? 'bg-green-100 text-green-800' : diffRealPercent < 0 ? 'bg-red-100 text-red-800' : ''}`}
                  style={{ minWidth: '48px', width: '48px', display: 'inline-block' }}>
                  {Math.round(diffRealPercent)}%
                </span>
              ) : (
                <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold text-center bg-gray-100 text-gray-400"
                  style={{ minWidth: '48px', width: '48px', display: 'inline-block' }}>
                  -
                </span>
              )}
            </div>
          </td>
        </tr>
      );
      if (isManagerExpanded) {
        Object.entries(clients).forEach(([client, clientRows]) => {
          const clientDataRows = clientRows as any[];
          const bookedX = clientDataRows.filter((r: any) => r.status === 'Booked' && r.request_year === yearX).length;
          const bookedY = clientDataRows.filter((r: any) => r.status === 'Booked' && r.request_year === yearY).length;
          const estX = clientDataRows.filter((r: any) => r.status === 'Booked' && r.request_year === yearX).reduce((sum: number, r: any) => sum + (r.estimated_revenue || 0), 0);
          const estY = clientDataRows.filter((r: any) => r.status === 'Booked' && r.request_year === yearY).reduce((sum: number, r: any) => sum + (r.estimated_revenue || 0), 0);
          const realX = clientDataRows.filter((r: any) => r.status === 'Booked' && r.request_year === yearX).reduce((sum: number, r: any) => sum + (r.realised_revenue || 0), 0);
          const realY = clientDataRows.filter((r: any) => r.status === 'Booked' && r.request_year === yearY).reduce((sum: number, r: any) => sum + (r.realised_revenue || 0), 0);
          const diffEst = estX - estY;
          const diffReal = realX - realY;
          const diffEstPercent = estY !== 0 ? ((diffEst / estY) * 100) : null;
          const diffRealPercent = realY !== 0 ? ((diffReal / realY) * 100) : null;
          if (bookedX === 0 && estX === 0 && estY === 0 && realX === 0 && realY === 0) return;
          
          rows.push(
            <tr key={`${manager}||${client}`} className="bg-white border-l-4 border-blue-200">
              <td className="pl-8 pr-2 py-2 whitespace-nowrap text-xs text-gray-700 min-w-[200px] font-normal">
                <span className="flex items-center">
                  <BuildingOffice2Icon className="w-4 h-4 mr-1 text-blue-400 flex-shrink-0" />
                  {client}
                </span>
              </td>
              <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{bookedX}</td>
              <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{bookedY}</td>
              <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{formatCurrency(estX)}</td>
              <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{formatCurrency(estY)}</td>
              <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{formatCurrency(realX)}</td>
              <td className="px-2 py-2 whitespace-nowrap text-xs text-center">{formatCurrency(realY)}</td>
              <td className="px-2 py-2 whitespace-nowrap text-xs">
                <div className="flex flex-row items-center gap-2 justify-end">
                  <span>{formatCurrency(diffEst)}</span>
                  {diffEstPercent !== null ? (
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-center ${diffEstPercent > 0 ? 'bg-green-100 text-green-800' : diffEstPercent < 0 ? 'bg-red-100 text-red-800' : ''}`}
                      style={{ minWidth: '48px', width: '48px', display: 'inline-block' }}>
                      {Math.round(diffEstPercent)}%
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold text-center bg-gray-100 text-gray-400"
                      style={{ minWidth: '48px', width: '48px', display: 'inline-block' }}>
                      -
                    </span>
                  )}
                </div>
              </td>
              <td className="px-2 py-2 whitespace-nowrap text-xs">
                <div className="flex flex-row items-center gap-2 justify-end">
                  <span>{formatCurrency(diffReal)}</span>
                  {diffRealPercent !== null ? (
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-center ${diffRealPercent > 0 ? 'bg-green-100 text-green-800' : diffRealPercent < 0 ? 'bg-red-100 text-red-800' : ''}`}
                      style={{ minWidth: '48px', width: '48px', display: 'inline-block' }}>
                      {Math.round(diffRealPercent)}%
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold text-center bg-gray-100 text-gray-400"
                      style={{ minWidth: '48px', width: '48px', display: 'inline-block' }}>
                      -
                    </span>
                  )}
                </div>
              </td>
            </tr>
          );
        });
      }
    });
    return rows;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Header />
      <main className="max-w-[90rem] mx-auto py-6 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="page-title">Created & Confirmed Revenue per Manager / Client</h1>
          <div className="flex items-center space-x-4">
            <ExportButton
              reportName="Created Confirmed Revenue per Manager"
              data={data}
              filters={{
                selectedCreationYears: appliedCreationYears,
                selectedArrivalYears: appliedArrivalYears,
                selectedManagers: appliedManagers,
                selectedSegments: appliedSegments,
                selectedClients: appliedClients,
                selectedHotels: appliedHotels,
                selectedHotelGroups: appliedHotelGroups,
                selectedArrivalMonths: appliedArrivalMonths,
                selectedArrivalQuarters: appliedArrivalQuarters,
                selectedCreationMonths: appliedCreationMonths,
                selectedCreationQuarters: appliedCreationQuarters,
                allClientsSelected: appliedClients.size === 0,
                allHotelsSelected: appliedHotels.size === 0,
                allHotelGroupsSelected: appliedHotelGroups.size === 0
              }}
              tableDataTransformer={getTableDataForExport}
            />
            <CacheStatus />
          </div>
        </div>
        <div className="mb-6">
          <FilterPanel
            selectedCreationYears={pendingCreationYears}
            selectedArrivalYears={pendingArrivalYears}
            selectedManagers={pendingManagers}
            selectedSegments={pendingSegments}
            selectedClients={pendingClients}
            selectedHotels={pendingHotels}
            selectedHotelGroups={pendingHotelGroups}
            selectedMarkets={pendingMarkets}
            availableCreationYears={availableCreationYears}
            availableArrivalYears={availableArrivalYears}
            availableManagers={availableManagers}
            availableSegments={availableSegments}
            clients={clients}
            hotels={hotels}
            hotelGroups={hotelGroups}
            markets={markets}
            setSelectedCreationYears={setPendingCreationYears}
            setSelectedArrivalYears={setPendingArrivalYears}
            setSelectedManagers={setPendingManagers}
            setSelectedSegments={setPendingSegments}
            resetFilters={resetFilters}
            selectedArrivalMonths={pendingArrivalMonths}
            selectedArrivalQuarters={pendingArrivalQuarters}
            setSelectedArrivalMonths={setPendingArrivalMonths}
            setSelectedArrivalQuarters={setPendingArrivalQuarters}
            selectedCreationMonths={pendingCreationMonths}
            selectedCreationQuarters={pendingCreationQuarters}
            setSelectedCreationMonths={setPendingCreationMonths}
            setSelectedCreationQuarters={setPendingCreationQuarters}
            onClientsChange={handleClientsChange}
            allClientsSelected={allClientsSelected}
            onHotelsChange={handleHotelsChange}
            allHotelsSelected={allHotelsSelected}
            onHotelGroupsChange={handleHotelGroupsChange}
            allHotelGroupsSelected={allHotelGroupsSelected}
            onMarketsChange={handleMarketsChange}
            allMarketsSelected={allMarketsSelected}
            onApplyFilters={handleApplyFilters}
            hasUnappliedChanges={hasUnappliedChanges}
          />
        </div>
        {loadingProgress && (
          <div className="mb-4 bg-blue-50 text-blue-700 p-2 rounded-md text-center">
            {loadingProgress}
          </div>
        )}

        <RevenueGraph data={filteredData} useRequestMonth={true} selectedYears={[yearX, yearY]} chartType="bar" />

        <div className="flex gap-4 mb-4 items-end justify-between">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 w-full"
            />
          </div>
          <div className="flex gap-4 items-center">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-700">Request Year X</label>
              <select value={yearX} onChange={e => setYearX(Number(e.target.value))} className="border rounded px-2 py-1">
                {availableCreationYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-700">Request Year Y</label>
              <select value={yearY} onChange={e => setYearY(Number(e.target.value))} className="border rounded px-2 py-1">
                {availableCreationYears.map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <colgroup>
                <col style={{ width: clientColumnWidth }} />
                <col style={{ width: bookedColumnWidth }} />
                <col style={{ width: bookedColumnWidth }} />
                <col style={{ width: revenueColumnWidth }} />
                <col style={{ width: revenueColumnWidth }} />
                <col style={{ width: revenueColumnWidth }} />
                <col style={{ width: revenueColumnWidth }} />
                <col style={{ width: diffColumnWidth }} />
                <col style={{ width: diffColumnWidth }} />
              </colgroup>
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('manager')}
                  >
                    <div className="flex items-center gap-1">
                      MANAGER
                      {sortConfig?.key === 'manager' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('bookedX')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      GROUPS {yearX}
                      {sortConfig?.key === 'bookedX' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('bookedY')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      GROUPS {yearY}
                      {sortConfig?.key === 'bookedY' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('estX')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      ESTIMATED {yearX}
                      {sortConfig?.key === 'estX' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('estY')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      ESTIMATED {yearY}
                      {sortConfig?.key === 'estY' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('realX')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      FINAL {yearX}
                      {sortConfig?.key === 'realX' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('realY')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      FINAL {yearY}
                      {sortConfig?.key === 'realY' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('diffEst')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      ESTIMATED DIFF
                      {sortConfig?.key === 'diffEst' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('diffReal')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      FINAL DIFF
                      {sortConfig?.key === 'diffReal' && (
                        <span>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {renderRows()}
                {/* Add the total row if there are 2 or more visible rows */}
                {sortedData.length >= 2 && (
                  <tr className="bg-gray-50 border-t-2 border-gray-200 font-bold">
                    <td className="px-2 py-1.5 text-sm text-gray-700">TOTAL</td>
                    <td className="px-2 py-1.5 text-xs text-center">{totals.bookedX}</td>
                    <td className="px-2 py-1.5 text-xs text-center">{totals.bookedY}</td>
                    <td className="px-2 py-1.5 text-xs text-center font-bold">{formatCurrency(totals.estX)}</td>
                    <td className="px-2 py-1.5 text-xs text-center font-bold">{formatCurrency(totals.estY)}</td>
                    <td className="px-2 py-1.5 text-xs text-center font-bold">{formatCurrency(totals.realX)}</td>
                    <td className="px-2 py-1.5 text-xs text-center font-bold">{formatCurrency(totals.realY)}</td>
                    <td className="px-2 py-1.5 text-xs text-center">
                      <div className="flex flex-row items-center gap-2 justify-end">
                        <span>{formatCurrency(totals.diffEst)}</span>
                        {totals.estY > 0 && (
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-center ${
                            totals.diffEst > 0 ? 'bg-green-100 text-green-800' : totals.diffEst < 0 ? 'bg-red-100 text-red-800' : ''
                          }`}
                            style={{ minWidth: '48px', width: '48px', display: 'inline-block' }}>
                            {Math.round((totals.diffEst / totals.estY) * 100)}%
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1.5 text-xs text-center">
                      <div className="flex flex-row items-center gap-2 justify-end">
                        <span>{formatCurrency(totals.diffReal)}</span>
                        {totals.realY > 0 && (
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold text-center ${
                            totals.diffReal > 0 ? 'bg-green-100 text-green-800' : totals.diffReal < 0 ? 'bg-red-100 text-red-800' : ''
                          }`}
                            style={{ minWidth: '48px', width: '48px', display: 'inline-block' }}>
                            {Math.round((totals.diffReal / totals.realY) * 100)}%
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}